import type { VenueReservation } from "@/graphql/operations";
import { getReservationStatusLowerCase } from "@/hooks/useReservation";
import icons from "@/tools/icons";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { calendarLayout } from "./calendarConstants";

type ReservationCalendarReservationTableProps = {
  dayReservations: VenueReservation[];
  maxVisibleReservations: number;
  canSelectReservation: boolean;
  selectReservation?: (reservationId: string) => void;
};

type ReservationRowProps = {
  reservation: VenueReservation;
  canSelectReservation: boolean;
  onSelect?: (reservationId: string) => void;
};

function ReservationRow({
  reservation,
  canSelectReservation,
  onSelect,
}: ReservationRowProps) {
  const timeLabel = (reservation.reservationTime ?? "").slice(0, 5);
  const status = getReservationStatusLowerCase(reservation);
  const statusColor =
    status === "accepted"
      ? "success.main"
      : status === "rejected"
        ? "error.main"
        : status === "pending"
          ? "warning.main"
          : "text.disabled";
  const dotSize = 8;

  return (
    <TableRow
      hover={canSelectReservation}
      role={canSelectReservation ? "button" : undefined}
      tabIndex={canSelectReservation ? 0 : -1}
      onClick={
        canSelectReservation
          ? (event) => {
              event.stopPropagation();
              onSelect?.(reservation.id);
            }
          : undefined
      }
      onKeyDown={
        canSelectReservation
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") {
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              onSelect?.(reservation.id);
            }
          : undefined
      }
      sx={{
        cursor: canSelectReservation ? "pointer" : "default",
        ...(canSelectReservation && {
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }),
        pointerEvents: canSelectReservation ? "auto" : "none",
        height: calendarLayout.reservationRowHeight,
      }}
    >
      <TableCell padding="none">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: dotSize,
              height: dotSize,
              borderRadius: "50%",
              backgroundColor: statusColor,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              whiteSpace: "nowrap",
              textAlign: "left",
              color: status === "rejected" ? "text.disabled" : "text.primary",
            }}
          >
            {timeLabel}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell padding="none" align="right" sx={{ whiteSpace: "nowrap" }}>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
          className="calendar-cell-count"
        >
          <Typography variant="caption">
            {reservation.numGuests || "?"}
          </Typography>
          <icons.Person fontSize="inherit" />
        </Stack>
      </TableCell>
      <TableCell
        padding="none"
        align="right"
        sx={{ whiteSpace: "nowrap", alignItems: "center" }}
      >
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
          className="calendar-cell-count"
        >
          <Typography variant="caption">
            {reservation.numGuides || "?"}
          </Typography>
          <icons.Flag fontSize="inherit" />
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export default function ReservationCalendarReservationTable({
  dayReservations,
  maxVisibleReservations,
  canSelectReservation,
  selectReservation,
}: ReservationCalendarReservationTableProps) {
  const hasOverflow = dayReservations.length > maxVisibleReservations;
  const visibleCount = hasOverflow
    ? Math.max(0, maxVisibleReservations - 1)
    : maxVisibleReservations;
  const visibleReservations = dayReservations.slice(0, visibleCount);
  const remainingCount = dayReservations.length - visibleCount;

  return (
    <Table
      size="small"
      sx={{
        width: "100%",
        maxWidth: "180px",
        tableLayout: "auto",
        "& td": { borderBottom: "none", paddingY: 0 },
      }}
    >
      <TableBody>
        {visibleReservations.map((reservation) => (
          <ReservationRow
            key={reservation.id}
            reservation={reservation}
            canSelectReservation={canSelectReservation}
            onSelect={selectReservation}
          />
        ))}
        {remainingCount > 0 && (
          <TableRow sx={{ height: calendarLayout.reservationRowHeight }}>
            <TableCell padding="none" colSpan={3}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                +{remainingCount}{" "}
                <Box
                  component="span"
                  sx={{
                    "@container (max-width: 92px)": {
                      display: "none",
                    },
                  }}
                >
                  reservations
                </Box>
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
